/**
 * COMPONENT_SPEC §2.1 `AppShell`. OWNER: web-frontend-engineer.
 *
 * The one persistent frame: ground, margins, navigation, skip link, live
 * regions, footer. It is the component that makes the ground switch WHOLESALE,
 * which is how RULING-002 stops being a per-screen judgement.
 *
 * `KEY-ORDER`: skip → house name → navigation → person's name → sign out →
 * page `h1` → main content in visual order → footer (`EL-HUMAN`).
 */

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import type { Role } from '@pt/core';
import { t } from '@pt/copy';
import { useAnnouncer } from './Announcer.tsx';
import { useNavigation } from './Router.tsx';
import { useHouse, useSession } from './SessionProvider.tsx';
import { FactLine, Link, Statement } from '../components/primitives.tsx';

const CLIENT_NAV = [
  { href: '/wardrobe', key: 'global.nav.wardrobe' },
  { href: '/measurements', key: 'global.nav.measurements' },
  { href: '/book', key: 'global.nav.book' },
  { href: '/commission', key: 'global.nav.commission' },
] as const;

/**
 * THE NAV IS SPLIT BY WHAT A ROLE CAN ACTUALLY REACH, and it was not before:
 * `swatches` and `clients` are BACKOFFICE routes that sat in the one staff nav,
 * so the workshop and the fitter were shown three doors that refuse them. The
 * refusal is correct and the sweep proves it still renders — but offering a
 * person a room and then turning them away at it is the app wasting their time
 * on purpose. The glossary joins the back-office half for the same reason a
 * signature is not the workshop's to give: it carries a name onto every sheet.
 */
const STAFF_NAV = [
  { href: '/atelier/orders', key: 'global.nav.orders' },
  { href: '/atelier/production', key: 'global.nav.production' },
  { href: '/atelier/cloth', key: 'global.nav.cloth' },
] as const;

const BACKOFFICE_NAV = [
  /* WIRE-1: the inbox first — what arrived is the thing to look at. */
  { href: '/atelier/leads', key: 'global.nav.leads' },
  { href: '/atelier/swatches', key: 'global.nav.swatches' },
  { href: '/atelier/clients', key: 'global.nav.clients' },
  { href: '/atelier/glossary', key: 'global.nav.glossary' },
  { href: '/atelier/retention', key: 'global.nav.retention' },
] as const;

export type Ground = 'paper' | 'book';

export interface AppShellProps {
  readonly title: string;
  readonly ground?: Ground;
  readonly width?: 'page' | 'ledger' | 'login';
  readonly children: ReactNode;
  /** Rendered above the h1, in the content column. `ST-OFFLINE` lives here. */
  readonly banner?: ReactNode;
  readonly chrome?: boolean;
}

/**
 * LAW 60 (19 October 2026) · THE DEMONSTRATION BAND. On the demonstration office
 * every session says `demonstration: true`, and this stands on every screen,
 * above the role bar, so nobody mistakes a placeholder client for a man or a
 * fiction for an order (D-028(e)). It does not print: a ticket printed from the
 * demonstration is still a ticket.
 */
export function DemonstrationBand({ session }: { session: { readonly demonstration?: boolean } | null }) {
  if (session?.demonstration !== true) return null;
  return (
    <div className="pt-chrome__demonstration pt-noprint" role="note">
      <FactLine emphasis>{t('staff.demonstration')}</FactLine>
    </div>
  );
}

export function AppShell({
  title,
  ground = 'paper',
  width = 'page',
  children,
  banner,
  chrome = true,
}: AppShellProps) {
  const { session, signOut } = useSession();
  const { navigate, match } = useNavigation();
  const { polite, assertive, say } = useAnnouncer();
  const house = useHouse();
  const headingRef = useRef<HTMLHeadingElement>(null);

  const staff = session !== null && session.role !== 'client';
  const backOffice = session?.role === 'master_tailor' || session?.role === 'admin';
  const nav =
    session === null
      ? []
      : staff
        ? [...STAFF_NAV, ...(backOffice ? BACKOFFICE_NAV : [])]
        : CLIENT_NAV;

  // KEY-ROUTE: focus to the page h1, the title updates carrying NO body data
  // ([T-33]), and the page name is announced once, politely.
  useEffect(() => {
    document.title = `${title} · ${t('global.houseName')}`;
    headingRef.current?.focus();
    say(t('global.announce.pageLoaded', { pageName: title }));
  }, [match.path, title, say]);

  // L-B: the ground switch is wholesale, including the chrome.
  useEffect(() => {
    document.documentElement.dataset['ground'] = ground;
    if (staff) document.documentElement.dataset['density'] = 'back-office';
    else delete document.documentElement.dataset['density'];
  }, [ground, staff]);

  return (
    <>
      <a className="pt-skip" href="#pt-main">
        {t('global.skipLink')}
      </a>

      {chrome ? (
        <header className="pt-chrome" role="banner">
          <a
            className="pt-chrome__house"
            href={session === null ? '/' : staff ? '/atelier/orders' : '/wardrobe'}
            onClick={(event) => {
              event.preventDefault();
              navigate(session === null ? '/' : staff ? '/atelier/orders' : '/wardrobe');
            }}
          >
            {staff ? t('global.backOfficeName', { houseName: t('global.houseName') }) : t('global.houseName')}
          </a>
          <nav className="pt-chrome__nav" role="navigation" aria-label={t('global.rooms.heading')}>
            {nav.map((entry) => (
              <a
                key={entry.href}
                href={entry.href}
                aria-current={match.path.startsWith(entry.href) ? 'page' : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  navigate(entry.href);
                }}
              >
                {t(entry.key)}
              </a>
            ))}
          </nav>
          {session === null ? null : (
            <div className="pt-chrome__principal">
              <span>
                {staff
                  ? t('global.chrome.staff', {
                      staffName: session.displayName,
                      roleLabel: roleLabel(session.role),
                    })
                  : session.displayName}
              </span>
              <button
                type="button"
                className="pt-units__option"
                onClick={() => {
                  void signOut();
                }}
              >
                {t('global.nav.signOut')}
              </button>
            </div>
          )}
        </header>
      ) : null}

      {/* ST-STAFF-ROLEBAR — honest to staff, and it is what makes the
          client-facing "access is recorded" claim true in practice ([T-78]). */}
      <DemonstrationBand session={session} />
      {staff ? (
        <div className="pt-chrome__rolebar">
          <FactLine>{t('staff.rolebar.audit')}</FactLine>
        </div>
      ) : null}

      <main
        id="pt-main"
        className={`pt-main${width === 'ledger' ? ' pt-main--ledger' : width === 'login' ? ' pt-main--login' : ''}`}
        role="main"
      >
        {banner}
        <h1 className="type-role-page-title pt-page__title" tabIndex={-1} ref={headingRef}>
          {title}
        </h1>
        {children}
      </main>

      {/* Exactly ONE polite region and exactly ONE assertive region per page. */}
      <div className="pt-live" aria-live="polite" aria-atomic="true">
        {polite}
      </div>
      <div className="pt-live" aria-live="assertive" aria-atomic="true">
        {assertive}
      </div>

      <footer className="pt-foot" role="contentinfo">
        <HumanRoute />
      </footer>
    </>
  );

  function roleLabel(role: Role): string {
    switch (role) {
      case 'master_tailor':
        return t('global.role.master_tailor');
      case 'admin':
        return t('global.role.admin');
      case 'production':
        return t('global.role.production');
      case 'fitter':
        return t('global.role.fitter');
      default:
        return t('global.role.client');
    }
  }

  function HumanRoute() {
    // EL-HUMAN: a person, a role, an address. Never a chat bubble, never a bot,
    // never a ticket form, never `support@`. Where the server has not supplied
    // a named contact we say nothing rather than invent one.
    if (house.contact === null) return null;
    const address = house.contact.address.trim();
    // A `mailto:` is only honest when the address is actually an email. An
    // empty or postal address must NEVER become `href="mailto:"` — a link that
    // resolves to nothing is the exact defect credibility.md flagged. A postal
    // address renders as plain text; an absent one is simply omitted, leaving
    // the NAMED person and role as the route rather than a broken link.
    const isEmail = address !== '' && address.includes('@');
    return (
      <div>
        <FactLine>{t('el.human.lead')}</FactLine>
        <FactLine>
          {t('el.human.person', {
            contactName: house.contact.displayName,
            contactRole: house.contact.role,
          })}
        </FactLine>
        {address === '' ? null : isEmail ? (
          <FactLine>
            <Link href={`mailto:${address}`}>
              {t('el.human.address', { contactAddress: address })}
            </Link>
          </FactLine>
        ) : (
          <FactLine>{t('el.human.address', { contactAddress: address })}</FactLine>
        )}
      </div>
    );
  }
}

/** `ST-OFFLINE` — an `EL-STATEMENT` at the top of the content column. Nothing
 * already on screen is removed or greyed, and no action that would write is
 * attempted. Never a toast, banner overlay or modal. */
export function OfflineBanner() {
  return <Statement>{t('offline.statement')}</Statement>;
}
